/**
 * The story engine: a journey (a record's documents through all its runs, or one run's) as phases in
 * story time — waits slow, runs quick, never real time — the comic panel told at each stop, and where
 * the documents are on the map at any moment. Pure: the map comes in as MapGeometry (src/map/types.ts),
 * nothing here touches the DOM. Moved from components/data/Playback.tsx and re-pointed at the new map.
 */
import dayjs from 'dayjs';
import type { Rhythm } from '../lib/rhythm.ts';
import type { MapGeometry, MapNode } from '../map/types.ts';
import { article, count, duration } from '../lib/format.ts';
import { stripSystem, systemName } from '../lib/systems.ts';
import { noun } from '../design/primitives.tsx';

/** Where a document is stored (the same shape as a card message or a hit, so those pass straight through). */
export interface DocRef { vaultId: string; uuid: string; day?: string }

/** A document of a journey: where it was stored, when, by which run, and what the run said of it. */
export interface PlayDoc {
  cls: string; type?: string | null; at: number; executionId: string | null; process: string | null; status: string | null;
  info?: string | null; ref: DocRef;
}
/** What is told: a record's documents through all its runs, or one run's. */
export interface Journey { title: string; kind: 'record' | 'run'; docs: PlayDoc[] }

/** What the engine needs of an integration; a MapFlow (and the old Integration) is one. */
export interface StoryFlow { process: string; from: string; to: string | null; external?: string | null; rhythm?: Rhythm | null }

/** What the story knows of the map's nodes and flows, without the geometry. */
export interface StoryContext {
  system(cls: string): string;
  format(cls: string): string | null;
  rhythm(process: string): Rhythm | null;
}

/** The context from the map's nodes and flows; a class not on the map falls back to its name's prefix. */
export function contextOf(nodes: MapNode[], flows: StoryFlow[]): StoryContext {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  return {
    system: (cls) => byId.get(cls)?.system ?? cls.slice(0, 3).toUpperCase(),
    format: (cls) => byId.get(cls)?.format ?? null,
    rhythm: (process) => flows.find((f) => f.process === process)?.rhythm ?? null,
  };
}

// ---------------------------------------------------------------- phases

export interface Leg {
  to: string | null; external: string | null; inN: number; outN: number; failed: boolean;
  inType?: string | null; outType?: string | null; outs: PlayDoc[];
}
export type Phase = { anim: number; hold: number } & (
  | { kind: 'start'; cls: string; at: number; n: number; doc: PlayDoc; first: boolean }
  | { kind: 'wait'; cls: string; from: number; to: number; n: number; next: string | null; arrives?: string }
  | { kind: 'run'; process: string | null; from: string; legs: Leg[]; start: number; end: number; failed: boolean; ins: PlayDoc[]; all: PlayDoc[] }
  | { kind: 'end'; at: number; first: number; runs: number; classes: string[]; unresolved: Unresolved | null });

/** A failure no later run of the same integration made good: what never got where it was going, and the document that failed. */
export interface Unresolved { cls: string; to: string | null; external: string | null; from: string; doc: PlayDoc | null }

/** Seconds of animation for a real time: slow where it took long, never real time (a log scale). */
const waitAnim = (ms: number) => Math.min(5, Math.max(0.5, 0.5 + 0.85 * Math.log10(1 + ms / 1000)));
const runAnim = (ms: number) => Math.min(4.5, 2 + 0.5 * Math.log10(1 + ms / 1000));
const MAX_RUNS = 40;
/** A story skips a wait shorter than this: it would only hold up the next stop. */
const MIN_STORY_WAIT = 30_000;
/** How long something took, as a story says it: whole seconds ("2 s"), then the usual units. */
export const took = (ms: number) => (ms < 1000 ? 'under a second' : ms < 60_000 ? `${Math.round(ms / 1000)} s` : duration(ms));
const uniq = (ds: PlayDoc[]) => new Set(ds.map((d) => d.ref.uuid)).size;
export const clamp = (v: number, a = 0, b = 1) => Math.min(b, Math.max(a, v));

/**
 * The journey as phases: the documents of each run together, the runs in time order. Before a run its
 * input waits in its data model (from when it got there to when the run started); in the run it moves
 * along the integration's arrow and becomes the run's output, one or many (a split), or many become one.
 * As a story every stop holds for a while, to tell what happened there. `systemOf` tells where a document
 * that turns up somewhere new (a delivery in the warehouse) was until then: in its own system if possible.
 */
export function phasesOf(docs: PlayDoc[], flows: StoryFlow[], story = true, systemOf?: (cls: string) => string): Phase[] {
  const hold = (s: number) => (story ? s : 0);
  const groups = new Map<string, PlayDoc[]>();
  for (const d of docs) {
    const k = d.executionId ?? `doc:${d.ref.uuid}`;
    groups.set(k, [...(groups.get(k) ?? []), d]);
  }
  const runs = [...groups.values()].map((g) => g.sort((a, b) => a.at - b.at)).sort((a, b) => a[0].at - b[0].at).slice(0, MAX_RUNS);
  const known = new Map<string, { at: number; n: number }>();
  const out: Phase[] = [];
  // The story's clock: the moment told last. A wait is told from there, never from before it (the strip
  // and the clock only ever move forward), and a failure is unresolved until a later run of its
  // integration made it good.
  let clock = 0;
  const unresolved = new Map<string, Unresolved>();
  const wait = (cls: string, from: number, to: number, n: number, next: string | null, arrives?: string) => {
    from = Math.max(from, clock);
    if (to <= from || (story && to - from < MIN_STORY_WAIT)) return;
    out.push({ kind: 'wait', cls, from, to, n, next, arrives, anim: story ? Math.max(3.4, waitAnim(to - from)) : waitAnim(to - from), hold: 0 });
    clock = Math.max(clock, from);
  };
  let first = true;
  for (const g of runs) {
    const process = g[0].process;
    const own = flows.filter((f) => f.process === process);
    const has = (cls: string) => g.some((d) => d.cls === cls);
    // Where the run read from: the input it stored, or, when it stored none, the known data model its
    // documents' flow starts at (a split's lines, an invoice made of an order)
    const from = own.find((f) => has(f.from))?.from
      ?? (own.find((f) => f.to && has(f.to) && known.has(f.from)) ?? flows.find((f) => f.to && has(f.to) && known.has(f.from)))?.from
      ?? g[0].cls;
    const start = g[0].at;
    const end = g[g.length - 1].at;
    const ins = g.filter((d) => d.cls === from);
    const before = known.get(from);
    if (!before) {
      // It turns up somewhere new: until then it was where it last got to in that system, else anywhere
      const sys = systemOf?.(from);
      const entries = [...known.entries()];
      const same = sys ? entries.filter(([cls]) => systemOf!(cls) === sys) : [];
      const latest = (same.length ? same : entries).reduce<[string, { at: number; n: number }] | undefined>((m, e) => (!m || e[1].at >= m[1].at ? e : m), undefined);
      if (latest && start > latest[1].at) wait(latest[0], latest[1].at, start, latest[1].n, process, from);
      out.push({ kind: 'start', cls: from, at: start, n: Math.max(1, ins.length), doc: ins[0] ?? g[0], first, anim: 0.6, hold: hold(first ? 3.6 : 3) });
      clock = Math.max(clock, start);
      first = false;
    } else if (start > before.at) wait(from, before.at, start, before.n, process);
    const byCls = new Map<string, PlayDoc[]>();
    for (const d of g) if (d.cls !== from) byCls.set(d.cls, [...(byCls.get(d.cls) ?? []), d]);
    const inN = Math.max(1, uniq(ins));
    const legs: Leg[] = [...byCls.entries()].map(([to, ds]) => ({
      to, external: null, inN, outN: uniq(ds), failed: ds.some((d) => d.status === 'FAILED'), inType: ins[0]?.type, outType: ds[0].type, outs: ds,
    }));
    const failed = g.some((d) => d.status === 'FAILED');
    if (legs.length === 0) {
      // Nothing stored on the other side: it went where the integration leads, or out of the map
      const target = own.find((f) => f.to && f.to !== from);
      const ext = own.find((f) => !f.to || f.to === from);
      if (target) legs.push({ to: target.to, external: null, inN, outN: 0, failed, inType: ins[0]?.type, outType: null, outs: [] });
      else legs.push({ to: null, external: ext?.external ?? null, inN, outN: inN, failed, inType: ins[0]?.type, outType: ins[0]?.type, outs: [] });
    }
    out.push({ kind: 'run', process, from, legs, start, end, anim: runAnim(end - start), hold: hold(4.8), failed, ins, all: g });
    clock = Math.max(clock, end);
    if (process) {
      const bad = legs.find((l) => l.failed);
      if (bad) {
        const failedDoc = g.find((d) => d.status === 'FAILED') ?? null;
        unresolved.set(process, { cls: failedDoc?.cls ?? from, to: bad.to, external: bad.external, from, doc: failedDoc });
      }
      else unresolved.delete(process);
    }
    // The input has been picked up: the next wait in that data model starts when this run ended
    known.set(from, { at: end, n: known.get(from)?.n ?? inN });
    for (const leg of legs) {
      if (!leg.to || leg.failed) continue;
      known.set(leg.to, { at: leg.outs.length ? leg.outs[leg.outs.length - 1].at : end, n: Math.max(1, leg.outN) });
    }
  }
  if (story && runs.length) {
    out.push({
      kind: 'end', at: runs[runs.length - 1].at(-1)!.at, first: runs[0][0].at, runs: runs.filter((g) => g[0].executionId).length,
      classes: [...new Set(runs.flat().map((d) => d.cls))], anim: 0.01, hold: 8, unresolved: [...unresolved.values()].at(-1) ?? null,
    });
  }
  return out;
}

// ---------------------------------------------------------------- the panels

export type Tone = 'start' | 'wait' | 'ok' | 'split' | 'merge' | 'fail' | 'out' | 'end';
export interface Panel {
  /** The node the panel is anchored to; null centres it over the map. */
  anchor: string | null;
  sticker: string;
  tone: Tone;
  /** A short headline (for the strip's label and assistive tech); the panel shows `text`. */
  title: string;
  /** What happened, in the run's own words where it has them. */
  text: string;
  /** For a wait: how often the next integration runs. */
  sub?: string;
  doc?: PlayDoc;
  at: number;
  /** For a wait: how long it lasts (ms), so the strip can fold consecutive waits into one chip. */
  waited?: number;
  system: string | null;
  /** The document's format going in and coming out of the run ("xml → json"). */
  formatIn: string | null;
  formatOut: string | null;
  /** "Wed 11:31 · 26 min later · in 2 s" */
  when: string;
  process: string | null;
}

const fmt = (type: string | null | undefined, fallback: string | null) => {
  const f = (type ?? fallback ?? '').toLowerCase();
  return f ? (f === 'text' ? 'txt' : f) : null;
};
const day = (ms: number) => dayjs(ms).format('ddd HH:mm');

/** What is told at a stop, from the data: the run's own words (info) where it has them. */
export function panelOf(ph: Phase, ctx: StoryContext, prev?: Panel): Panel {
  const sys = (cls: string) => systemName(ctx.system(cls));
  /** "26 min later": the time since the previous stop that was not a wait (the hop before this one). */
  const since = (at: number) => (prev && at - prev.at >= 60_000 ? `${duration(at - prev.at)} later` : null);
  if (ph.kind === 'start') {
    const f = fmt(ph.doc.type, ctx.format(ph.cls));
    return {
      anchor: ph.cls, sticker: ph.first ? 'Start!' : 'New!', tone: 'start', title: ph.first ? `It begins in ${sys(ph.cls)}` : `It turns up in ${sys(ph.cls)}`,
      system: ctx.system(ph.cls), text: ph.doc.info ?? `A ${noun(ph.cls)} is stored in ${sys(ph.cls)}.`, doc: ph.doc, at: ph.at,
      formatIn: f, formatOut: null, when: [day(ph.at), since(ph.at)].filter(Boolean).join(' · '), process: ph.doc.process,
    };
  }
  if (ph.kind === 'wait') {
    // Until the next run picks it up; or, when something new turns up elsewhere (a delivery), until that
    const r = ph.next && !ph.arrives ? ctx.rhythm(ph.next) : null;
    const until = ph.arrives ? `, until ${article(noun(ph.arrives))} ${noun(ph.arrives)} turns up in ${sys(ph.arrives)}`
      : ph.next ? ` until “${stripSystem(ph.next)}” picks ${ph.n > 1 ? 'them' : 'it'} up` : '';
    return {
      anchor: ph.cls, sticker: `Waiting ${took(ph.to - ph.from)}`, tone: 'wait', title: `It waits in ${sys(ph.cls)}`, system: ctx.system(ph.cls), at: ph.from, waited: ph.to - ph.from,
      sub: r && r.kind !== 'once' && ph.next ? `${stripSystem(ph.next)} runs ${r.label}` : undefined,
      text: `The ${noun(ph.cls, ph.n)} ${ph.n > 1 ? 'sit' : 'sits'} here for ${took(ph.to - ph.from)}${until}.`,
      formatIn: fmt(null, ctx.format(ph.cls)), formatOut: null, when: `${day(ph.from)} · waits ${took(ph.to - ph.from)}`, process: ph.next,
    };
  }
  if (ph.kind === 'end') {
    const systems = new Set(ph.classes.map(ctx.system)).size;
    const u = ph.unresolved;
    const summary = `${count(ph.runs)} run${ph.runs === 1 ? '' : 's'} through ${systems} system${systems === 1 ? '' : 's'}`;
    const span = `from ${dayjs(ph.first).format('ddd D.M. HH:mm')} to ${dayjs(ph.at).format('ddd D.M. HH:mm')}`;
    const base = { anchor: null, system: null, at: ph.at, formatIn: null, formatOut: null, process: null, when: `${day(ph.at)} · ${took(ph.at - ph.first)} from start to finish` };
    if (u) {
      // The consequence in business terms, with the reason in the run's own words:
      // "The invoice never reached the e-invoice operator · Maventa 401 Unauthorized, token expired"
      const place = (name: string) => (/^[a-z]/.test(name) ? `the ${name}` : name);
      const consequence = `The ${noun(u.from)} never ${u.external ? `reached ${place(u.external)}` : u.to ? `reached ${sys(u.to)}` : `left ${sys(u.from)}`}`;
      const reason = reasonOf(u.doc?.info);
      // Told beside the node the document never left, where the failed arrow starts
      return {
        ...base, anchor: u.from, system: ctx.system(u.from), sticker: 'Failed!', tone: 'fail', title: consequence,
        text: reason ? `${consequence} · ${reason}` : `${consequence}.`, sub: `${summary}, ${span}.`, doc: u.doc ?? undefined,
      };
    }
    return { ...base, sticker: 'The end', tone: 'end', title: summary, text: `${summary}, ${span}.` };
  }
  const leg = ph.legs[0];
  const proc = ph.process ? stripSystem(ph.process) : 'A run';
  const a = fmt(leg.inType, ctx.format(ph.from));
  const b = leg.to ? fmt(leg.outType, ctx.format(leg.to)) : a;
  const swapped = !!a && !!b && a !== b;
  const failedDoc = ph.all.find((d) => d.status === 'FAILED');
  const anchor = ph.failed ? (failedDoc?.cls ?? leg.to ?? ph.from) : (leg.to ?? ph.from);
  const base = {
    anchor, at: ph.end, system: ctx.system(anchor), formatIn: a, formatOut: leg.to ? b : null, process: ph.process,
    when: [day(ph.end), since(ph.end), `in ${took(ph.end - ph.start)}`].filter(Boolean).join(' · '),
  };
  if (ph.failed) {
    return { ...base, sticker: 'Failed!', tone: 'fail', title: `${proc} failed`, text: failedDoc?.info ?? `${proc} could not deliver it.`, doc: failedDoc ?? ph.ins[0] };
  }
  if (!leg.to) {
    return { ...base, sticker: 'Sent!', tone: 'out', title: `Sent out${leg.external ? ` to ${leg.external}` : ''}`, text: ph.all.at(-1)?.info ?? `${proc} sent it on${leg.external ? ` to ${leg.external}` : ''}.`, doc: ph.all.at(-1) };
  }
  if (leg.inN === 1 && leg.outN > 1) {
    return { ...base, sticker: 'Split!', tone: 'split', title: `1 ${noun(ph.from)} → ${count(leg.outN)} ${noun(leg.to, leg.outN)}`, text: leg.outs[0]?.info ?? `${proc} cut it into ${count(leg.outN)} ${noun(leg.to, leg.outN)} in ${sys(leg.to)}.`, doc: leg.outs[0] };
  }
  if (leg.inN > 1) {
    return { ...base, sticker: 'Merge!', tone: 'merge', title: `${count(leg.inN)} ${noun(ph.from, leg.inN)} → ${leg.outN === 1 ? 'one' : count(leg.outN)} ${noun(leg.to, leg.outN)}`, text: leg.outs[0]?.info ?? `${proc} put ${count(leg.inN)} ${noun(ph.from, leg.inN)} together.`, doc: leg.outs[0] };
  }
  return {
    ...base, sticker: swapped ? `${a!.toUpperCase()} → ${b!.toUpperCase()}` : 'Sent!', tone: 'ok', title: `Now in ${sys(leg.to)}`,
    text: leg.outs[0]?.info ?? ph.ins[0]?.info ?? `${proc} made a ${noun(leg.to)} of it.`, doc: leg.outs[0] ?? ph.ins[0],
  };
}

/**
 * Why it failed, from the run's own words: what follows "failed:" (or "error:"), a trailing parenthesis
 * folded in — "Invoice 963643 failed: Maventa 401 Unauthorized (token expired)" → "Maventa 401 Unauthorized, token expired".
 */
export function reasonOf(info: string | null | undefined): string | null {
  if (!info) return null;
  const m = /(?:failed|error|rejected|refused)\s*[:\u2013-]\s*(.+)$/i.exec(info);
  const reason = (m?.[1] ?? info).trim().replace(/\s*\(([^()]*)\)\s*\.?$/, ', $1').replace(/[.\s]+$/, '');
  return reason || null;
}

/** Every stop's panel, each knowing the hop before it (for "26 min later"; waits are not hops). */
export function panelsOf(phases: Phase[], ctx: StoryContext): Panel[] {
  const out: Panel[] = [];
  let hop: Panel | undefined;
  for (const ph of phases) {
    const panel = panelOf(ph, ctx, hop);
    out.push(panel);
    if (panel.tone !== 'wait') hop = panel;
  }
  return out;
}

// ---------------------------------------------------------------- the scene: where the documents are

export interface Token {
  x: number; y: number; format: string | null; failed: boolean; scale: number; opacity: number;
  /** How many documents the glyph stands for, when more than one sit together. */
  n?: number;
  /** A moving document's soft trail, a little behind it. */
  trail?: { x: number; y: number };
  /** Degrees; a failed document tilts as it drops. */
  tilt?: number;
}
export interface Scene {
  tokens: Token[];
  /** Nodes in play, ringed; failed ones in --fail. */
  rings: { id: string; failed: boolean }[];
  /** The incoming flows, lit over the map. */
  lit: { d: string; failed: boolean }[];
  /** "waits 15 h", counting up over the waiting document. */
  label: { x: number; y: number; text: string } | null;
}

type Pt = { x: number; y: number };
interface Route { d: string; at(t: number): Pt; swapAt: number }

const rounded = (n: number) => Math.round(n * 10) / 10;

/** The route of a leg: the map's own path for the flow, else a straight line (or a stub out of the map). */
function routeOf(geo: MapGeometry, from: string, to: string | null, process: string | null): Route | null {
  const rf = geo.flowBetween(from, to, process) ?? geo.flowBetween(from, to, null);
  const path = rf ? geo.path(rf.id) : null;
  if (rf && path) return { d: path.d, at: (t) => path.pointAt(clamp(t)), swapAt: rf.swapAt > 0 && rf.swapAt < 1 ? rf.swapAt : 0.5 };
  const a = geo.box(from);
  if (!a) return null;
  const b = to ? geo.box(to) : null;
  const p0 = { x: a.x + a.w, y: a.y + a.h / 2 };
  const p1 = b ? { x: b.x, y: b.y + b.h / 2 } : { x: a.x + a.w + 90, y: a.y + a.h / 2 };
  return {
    d: `M${rounded(p0.x)},${rounded(p0.y)} L${rounded(p1.x)},${rounded(p1.y)}`,
    at: (t) => ({ x: p0.x + (p1.x - p0.x) * clamp(t), y: p0.y + (p1.y - p0.y) * clamp(t) }),
    swapAt: 0.5,
  };
}

/** The unit normal of a route at t, from two samples (no assumptions about the path's angle unit). */
function normalAt(r: Route, t: number): Pt {
  const a = r.at(Math.max(0, t - 0.01));
  const b = r.at(Math.min(1, t + 0.01));
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l = Math.hypot(dx, dy) || 1;
  return { x: -dy / l, y: dx / l };
}

/**
 * Where everything is at progress p (0..1) of a phase: the documents sitting at a node's right edge or
 * moving along a flow, the nodes ringed and the flows lit. In a run the inputs travel to where the run
 * happens (the flow's swapAt), the run turns them into the outputs with a scale pulse (the format swaps
 * there), then the outputs travel on: a fan for a split, a fold for a merge, a drop for a failure.
 */
export function sceneOf(ph: Phase, p: number, holding: boolean, geo: MapGeometry, ctx: StoryContext): Scene {
  const scene: Scene = { tokens: [], rings: [], lit: [], label: null };
  const sit = (cls: string, n: number, format: string | null, pulse = 0) => {
    const b = geo.box(cls);
    if (!b) return null;
    scene.rings.push({ id: cls, failed: false });
    scene.tokens.push({ x: b.x + b.w, y: b.y + b.h / 2, format, failed: false, scale: 1 + pulse, opacity: 1, n: n > 1 ? n : undefined });
    return b;
  };
  if (ph.kind === 'start') {
    sit(ph.cls, ph.n, fmt(ph.doc.type, ctx.format(ph.cls)), 0.35 * Math.sin(Math.PI * Math.min(1, p)));
    return scene;
  }
  if (ph.kind === 'wait') {
    const b = sit(ph.cls, ph.n, fmt(null, ctx.format(ph.cls)), 0.06 * Math.sin(Math.PI * 4 * p));
    if (b) scene.label = { x: b.x + b.w, y: b.y + b.h / 2 - 24, text: `waits ${took((ph.to - ph.from) * p)}` };
    return scene;
  }
  if (ph.kind === 'end') return scene;

  scene.rings.push({ id: ph.from, failed: false });
  for (const leg of ph.legs) {
    const r = routeOf(geo, ph.from, leg.to, ph.process);
    if (!r) continue;
    scene.lit.push({ d: r.d, failed: leg.failed });
    if (leg.to) scene.rings.push({ id: leg.to, failed: leg.failed && leg.outs.length > 0 });
    if (leg.failed && leg.outs.length === 0) scene.rings[0] = { id: ph.from, failed: true };
    const a = fmt(leg.inType, ctx.format(ph.from));
    const b = leg.to ? fmt(leg.outType, ctx.format(leg.to)) : a;
    const IN = Math.min(leg.inN, 6);
    const OUT = Math.min(Math.max(1, leg.outN), 8);
    const s = r.swapAt;
    const gap = 0.07;
    if (p < 0.45) {
      // In: the inputs one after another (a merge folds them in from the side), meeting where the run happens
      for (let j = 0; j < IN; j++) {
        const u = clamp((p / 0.45) * (s + (IN - 1) * gap) - j * gap, 0, s);
        const at = r.at(u);
        const n = normalAt(r, u);
        const fold = IN > 1 ? (j - (IN - 1) / 2) * 11 * clamp(1 - u / s) : 0;
        scene.tokens.push({ x: at.x + n.x * fold, y: at.y + n.y * fold, format: a, failed: false, scale: 1, opacity: 1, trail: r.at(u - 0.035) });
      }
    } else if (p < 0.58) {
      // The run turns them into the output
      const k = (p - 0.45) / 0.13;
      const at = r.at(s);
      scene.tokens.push({ x: at.x, y: at.y, format: k < 0.5 ? a : b, failed: leg.failed && k >= 0.5, scale: 1 + 0.45 * Math.sin(Math.PI * k), opacity: 1 });
    } else {
      const q = (p - 0.58) / 0.42;
      if (leg.failed) {
        // It gets a little further, then drops off the arrow and lies there
        const u = s + (1 - s) * 0.25 * Math.min(1, q * 2.2);
        const at = r.at(u);
        const fall = Math.max(0, q - 0.35) / 0.65;
        scene.tokens.push({ x: at.x + 6 * fall, y: at.y + 34 * fall * fall, format: b, failed: true, scale: 1, opacity: 1, tilt: 22 * fall });
      } else {
        // Out: a split fans the outputs apart then folds them into the target; one document just goes
        const gap2 = 0.1;
        for (let j = 0; j < OUT; j++) {
          const u = s + (1 - s) * clamp(q * (1 + (OUT - 1) * gap2) - j * gap2);
          const n = normalAt(r, u);
          const spread = OUT > 1 ? (j - (OUT - 1) / 2) * 12 * Math.sin(Math.PI * clamp((u - s) / (1 - s))) : 0;
          const at = r.at(u);
          const rest = holding && u >= 1;
          scene.tokens.push({
            x: at.x + n.x * spread, y: at.y + n.y * spread, format: b, failed: false, scale: 1, opacity: leg.to ? 1 : 1 - clamp((u - 0.85) / 0.15),
            trail: rest ? undefined : r.at(u - 0.035), n: rest && OUT > 1 && j === OUT - 1 ? leg.outN : undefined,
          });
        }
      }
    }
  }
  return scene;
}
